import { playersAbove } from "../document/band.ts";
import { applyCommand, type Command } from "../document/commands.ts";
import type { InstanceFootprint, LayerDocument, MapDocument, PropBehaviour, PropInstance, PropLayer, TileRef, TilesetRef, WorldDocument } from "../document/types.ts";
import { propFeet } from "./objects.ts";

/**
 * Prop templates (ADR-066): a named set of props saved from one map and placed into any other. A piece is a
 * prop without a layer: its frame, flips, sort offset and behaviour, its offset in whole pixels from the
 * template's anchor, and a layer role read from the Players band (ADR-047), so a placement lands every
 * piece on the layer that plays that role in the target map, adding the ones it lacks, as one command.
 */

/**
 * Where a piece draws against the characters: `under` on a prop layer below the Players row, `with` on the
 * lowest prop layer in the band, `above` on a band prop layer over that one.
 */
export type TemplateRole = "under" | "with" | "above";

/**
 * The roles in the order a panel lists them.
 */
export const TEMPLATE_ROLES: readonly TemplateRole[] = ["under", "with", "above"];

/**
 * One prop of a template. `dx` and `dy` are whole pixels right of and below the template's anchor, which is
 * the bottom-left of the anchor piece's anchor cell. `sortPixels` is the prop's sort offset in whole pixels.
 * `sortRows` is set only on a piece read from a schema 1 file, whose offset was whole rows and which names no
 * tile size (ADR-098 decision 3): the rows are added, times the tile size, where the piece is placed, and a
 * template captured from placed props carries none.
 * `keepPlacement` is present, and true, on a piece captured from a prop kept as placed (ADR-040 amendment of
 * 2026-10-04), and the prop a placement makes of it is kept as placed too: its place against the other pieces
 * is the deliberate one.
 */
export interface TemplatePiece {
  readonly role: TemplateRole;
  readonly frame: TileRef;
  readonly dx: number;
  readonly dy: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly sortPixels: number;
  readonly sortRows?: number;
  readonly behaviour?: PropBehaviour;
  /** The piece's own footprint (ADR-040 amendment of 2026-10-04), cells of this piece's frame. */
  readonly footprint?: InstanceFootprint;
  readonly keepPlacement?: true;
}

/**
 * A template: its name and its pieces in draw order.
 */
export interface PropTemplate {
  readonly name: string;
  readonly pieces: readonly TemplatePiece[];
}

/**
 * A placed prop, by layer and id.
 */
export interface PropRef {
  readonly layerId: string;
  readonly propId: string;
}

/**
 * The prop layer each role resolves to in a map, or null where the map has none.
 */
export interface RoleLayers {
  readonly under: string | null;
  readonly with: string | null;
  readonly above: string | null;
}

/**
 * A role layer a placement added.
 */
export interface AddedLayer {
  readonly role: TemplateRole;
  readonly layerId: string;
  readonly name: string;
}

/**
 * What placing a template does: the one command to run, the props it places in piece order, the role
 * layers it adds and how many terrain layers the added `under` layer moves up (their random variations
 * re-resolve, ADR-021 decision 4).
 */
export interface TemplatePlacement {
  readonly command: Command;
  readonly props: readonly PropRef[];
  readonly added: readonly AddedLayer[];
  readonly shifted: number;
}

/**
 * Why a template cannot be placed in a world: the piece, by index in the template, and the reason.
 */
export interface TemplateIssue {
  readonly piece: number;
  readonly message: string;
}

/**
 * The names a placement gives the role layers it adds.
 */
export const ROLE_LAYER_NAMES: Readonly<Record<TemplateRole, string>> = { under: "Under players", with: "Props", above: "Above props" };

/**
 * The longest template name, in characters.
 */
export const MAX_TEMPLATE_NAME = 64;

/**
 * What a template id looks like: the world id rule, so it is safe as a file name.
 */
export const TEMPLATE_ID = /^[a-z0-9][a-z0-9-]{0,63}$/;

const MAX_ID = 64;
const FALLBACK_ID = "template";
const CONTROL = /[\u0000-\u001f\u007f]/;

/**
 * What is wrong with a template name, if anything. A behaviour preset's name follows the same rule and names
 * itself as a preset's.
 *
 * @param name The name.
 * @param noun What the name belongs to, as the problem says it.
 * @returns The problem, or null for a good name.
 */
export function templateNameProblem(name: string, noun = "template"): string | null {
  if (name.length === 0 || name.length > MAX_TEMPLATE_NAME) {
    return `a ${noun} name is 1 to ${MAX_TEMPLATE_NAME} characters`;
  }
  if (name.trim() !== name) {
    return `a ${noun} name has no space at either end`;
  }
  if (CONTROL.test(name)) {
    return `a ${noun} name has no control characters`;
  }
  return null;
}

/**
 * The id a template's file takes: its name in lower case with accents dropped and every run of other
 * characters a single hyphen, cut to 64 characters, then suffixed `-2`, `-3` and so on until no other
 * template holds it.
 *
 * @param name The template's name.
 * @param taken The ids other templates hold.
 * @returns The id.
 */
export function templateIdFor(name: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const slug =
    name
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, MAX_ID)
      .replace(/-+$/, "") || FALLBACK_ID;
  if (!used.has(slug)) {
    return slug;
  }
  for (let n = 2; ; n += 1) {
    const suffix = `-${n}`;
    const id = `${slug.slice(0, MAX_ID - suffix.length).replace(/-+$/, "")}${suffix}`;
    if (!used.has(id)) {
      return id;
    }
  }
}

/**
 * The index of the lowest prop layer in the Players band, or -1.
 *
 * @param map The map.
 * @returns The layer index.
 */
function withIndex(map: MapDocument): number {
  const band = playersAbove(map);
  return map.layers.findIndex((layer, index) => index >= band && layer.kind === "prop");
}

/**
 * The role a prop layer plays (ADR-066 decision 2): below the Players band it is `under`, the lowest prop
 * layer in the band is `with`, any band prop layer over it is `above`.
 *
 * @param map The map.
 * @param layerId The layer.
 * @returns The role.
 * @throws Error when the layer is missing or holds no props.
 */
export function layerRole(map: MapDocument, layerId: string): TemplateRole {
  const index = map.layers.findIndex((l) => l.id === layerId);
  const layer = map.layers[index];
  if (layer === undefined) {
    throw new Error(`layer ${layerId} does not exist`);
  }
  if (layer.kind !== "prop") {
    throw new Error(`layer ${layerId} is not a prop layer`);
  }
  if (index < playersAbove(map)) {
    return "under";
  }
  return index === withIndex(map) ? "with" : "above";
}

/**
 * The layer each role resolves to (ADR-066 decision 6): `under` the topmost prop layer below the Players
 * row, `with` the lowest prop layer in the band, `above` the topmost band prop layer over `with`.
 *
 * @param map The map.
 * @returns The layer ids, null for a role the map has no layer for.
 */
export function roleLayers(map: MapDocument): RoleLayers {
  const band = playersAbove(map);
  const lowest = withIndex(map);
  const topmost = (from: number, to: number): string | null => {
    for (let i = to; i >= from; i -= 1) {
      const layer = map.layers[i];
      if (layer?.kind === "prop") {
        return layer.id;
      }
    }
    return null;
  };
  return { under: topmost(0, band - 1), with: lowest < 0 ? null : (map.layers[lowest] as LayerDocument).id, above: lowest < 0 ? null : topmost(lowest + 1, map.layers.length - 1) };
}

/**
 * The anchor piece of a selection (ADR-066 decision 3): the leftmost, the lowest of equally leftmost ones,
 * the first in draw order of complete ties.
 *
 * @param props The selected props in draw order.
 * @returns The anchor prop.
 */
function anchorOf(props: readonly PropInstance[]): PropInstance {
  return props.reduce((best, prop) => (prop.x < best.x || (prop.x === best.x && prop.y > best.y) ? prop : best));
}

/**
 * Captures selected props as a template (ADR-066 decision 1): every piece in draw order with its role, its
 * offset in whole pixels from the anchor piece's anchor, its flips, sort offset and behaviour.
 *
 * @param map The map holding the props.
 * @param selection The selected props, in any order.
 * @param name The template's name.
 * @param tileSize The world's tile size in pixels.
 * @param roles Roles chosen for pieces by prop id, over the roles their layers give.
 * @returns The template.
 * @throws Error when the selection is empty, names a prop twice or one that is not on its layer, names a
 *   layer that holds no props, or the name is bad.
 */
export function captureTemplate(map: MapDocument, selection: readonly PropRef[], name: string, tileSize: number, roles: Readonly<Record<string, TemplateRole>> = {}): PropTemplate {
  const problem = templateNameProblem(name);
  if (problem !== null) {
    throw new Error(problem);
  }
  if (selection.length === 0) {
    throw new Error("a template needs at least one prop; the selection holds no props");
  }
  const chosen = new Set<string>();
  for (const ref of selection) {
    const key = `${ref.layerId}/${ref.propId}`;
    if (chosen.has(key)) {
      throw new Error(`prop ${ref.propId} is selected twice`);
    }
    chosen.add(key);
    const layer = map.layers.find((l) => l.id === ref.layerId);
    if (layer === undefined || layer.kind !== "prop") {
      throw new Error(`layer ${ref.layerId} holds no props`);
    }
    if (!layer.props.some((p) => p.id === ref.propId)) {
      throw new Error(`prop ${ref.propId} is not on layer ${ref.layerId}`);
    }
  }
  const pieces: { prop: PropInstance; role: TemplateRole }[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (chosen.has(`${layer.id}/${prop.id}`)) {
        pieces.push({ prop, role: roles[prop.id] ?? layerRole(map, layer.id) });
      }
    }
  }
  const anchor = anchorOf(pieces.map((p) => p.prop));
  return {
    name,
    pieces: pieces.map(({ prop, role }) => ({
      role,
      frame: { tileset: prop.frame.tileset, tile: prop.frame.tile },
      dx: Math.round((prop.x - anchor.x) * tileSize),
      dy: Math.round((prop.y - anchor.y) * tileSize),
      flipX: prop.flipX,
      flipY: prop.flipY,
      sortPixels: prop.sortPixels,
      ...(prop.behaviour === undefined ? {} : { behaviour: prop.behaviour }),
      ...(prop.footprint === undefined ? {} : { footprint: prop.footprint }),
      ...(prop.keepPlacement === true ? { keepPlacement: true as const } : {}),
    })),
  };
}

/**
 * Where each piece of a template stands for an anchor: the anchor plus the piece's offset, on the pixel
 * lattice.
 *
 * @param template The template.
 * @param anchor Where the anchor lands, in tiles.
 * @param tileSize Tile size in pixels.
 * @returns Each piece's anchor in tiles, in piece order.
 */
export function piecePositions(template: PropTemplate, anchor: { readonly x: number; readonly y: number }, tileSize: number): { readonly x: number; readonly y: number }[] {
  return template.pieces.map((piece) => ({ x: Math.round((anchor.x + piece.dx / tileSize) * tileSize) / tileSize, y: Math.round((anchor.y + piece.dy / tileSize) * tileSize) / tileSize }));
}

/**
 * Builds the placement of a template (ADR-066 decisions 6 and 7): the role layers the map lacks, then every
 * piece at the anchor plus its offset on its role's layer, appended in piece order, as one compound command
 * checked command by command against the document it will run on. A piece is refused where the compile
 * would refuse it: its anchor outside the map, or, for a piece carrying a behaviour the compile lowers to a
 * thing, its feet (`propFeet`, measured by the frame's width as the compile measures the initial state)
 * outside the map. A behaviour driven by presence (ADR-058 amendment, P1.3-T4) is never lowered, so its feet
 * are not asked for.
 *
 * @param world The world.
 * @param mapId The map to place into.
 * @param template The template.
 * @param anchor Where the anchor lands, in tiles on the pixel lattice.
 * @param allocateId Hands out an id unused in the map for a prefix, `layer` or `prop`.
 * @param frameWidth The width in pixels the compile gives a frame (its footprint's), 0 when it has none.
 * @param lowered Whether the compile lowers a behaviour to a thing standing at its feet: false only for one
 *   whose type is driven by presence.
 * @returns The placement.
 * @throws Error when the map is missing, the anchor is off the pixel lattice, a piece or a behaviour piece's
 *   feet would stand outside the map, or a command fails against the document (a frame on a tileset the
 *   world does not list).
 */
export function placeTemplate(world: WorldDocument, mapId: string, template: PropTemplate, anchor: { readonly x: number; readonly y: number }, allocateId: (prefix: string) => string, frameWidth: (frame: TileRef) => number = () => 0, lowered: (behaviour: PropBehaviour) => boolean = () => true): TemplatePlacement {
  const tileSize = world.tileSize;
  if (!Number.isInteger(anchor.x * tileSize) || !Number.isInteger(anchor.y * tileSize)) {
    throw new Error(`anchor (${anchor.x}, ${anchor.y}) is not on the pixel lattice of a ${tileSize} pixel tile`);
  }
  let current = world;
  const commands: Command[] = [];
  const run = (command: Command): void => {
    current = applyCommand(current, command);
    commands.push(command);
  };
  const map = (): MapDocument => {
    const found = current.maps.find((m) => m.id === mapId);
    if (found === undefined) {
      throw new Error(`map ${mapId} does not exist`);
    }
    return found;
  };
  const start = map();
  const inside = (point: { readonly x: number; readonly y: number }): boolean => point.x >= 0 && point.y >= 0 && point.x < start.width && point.y < start.height;
  const positions = piecePositions(template, anchor, tileSize);
  template.pieces.forEach((piece, index) => {
    const at = positions[index] as { readonly x: number; readonly y: number };
    const name = `piece ${index + 1} (${piece.frame.tileset} tile ${piece.frame.tile})`;
    if (!inside(at)) {
      throw new Error(`${name} would stand at (${at.x}, ${at.y}), outside the ${start.width}x${start.height} map`);
    }
    if (piece.behaviour !== undefined && lowered(piece.behaviour)) {
      const feet = propFeet(at, frameWidth(piece.frame), tileSize);
      if (!inside(feet)) {
        throw new Error(`${name} is a ${piece.behaviour.type} whose feet would stand at (${feet.x}, ${feet.y}), outside the ${start.width}x${start.height} map`);
      }
    }
  });
  const needed = new Set(template.pieces.map((p) => p.role));
  const added: AddedLayer[] = [];
  const newLayer = (role: TemplateRole): PropLayer => {
    const layer: PropLayer = { kind: "prop", id: allocateId("layer"), name: ROLE_LAYER_NAMES[role], props: [] };
    added.push({ role, layerId: layer.id, name: layer.name });
    return layer;
  };
  let shifted = 0;
  if (needed.has("under") && roleLayers(start).under === null) {
    const band = playersAbove(start);
    shifted = start.layers.filter((l, i) => i >= band && l.kind === "terrain").length;
    run({ kind: "addLayer", mapId, index: band, layer: newLayer("under") });
    run({ kind: "setPlayersAbove", mapId, before: playersAbove(map()), after: band + 1 });
  }
  if ((needed.has("with") || needed.has("above")) && roleLayers(map()).with === null) {
    run({ kind: "addLayer", mapId, index: map().layers.length, layer: newLayer("with") });
  }
  if (needed.has("above") && roleLayers(map()).above === null) {
    run({ kind: "addLayer", mapId, index: map().layers.length, layer: newLayer("above") });
  }
  const layers = roleLayers(map());
  const props: PropRef[] = [];
  template.pieces.forEach((piece, index) => {
    const layerId = layers[piece.role] as string;
    const layer = map().layers.find((l) => l.id === layerId) as PropLayer;
    const at = positions[index] as { readonly x: number; readonly y: number };
    const prop: PropInstance = {
      id: allocateId("prop"),
      frame: piece.frame,
      x: at.x,
      y: at.y,
      flipX: piece.flipX,
      flipY: piece.flipY,
      sortPixels: piece.sortPixels + (piece.sortRows ?? 0) * world.tileSize,
      ...(piece.behaviour === undefined ? {} : { behaviour: piece.behaviour }),
      ...(piece.footprint === undefined ? {} : { footprint: piece.footprint }),
      ...(piece.keepPlacement === true ? { keepPlacement: true as const } : {}),
    };
    run({ kind: "addProp", mapId, layerId, index: layer.props.length, prop });
    props.push({ layerId, propId: prop.id });
  });
  return { command: { kind: "compound", commands }, props, added, shifted };
}

/**
 * Why a template cannot be placed in a world (ADR-066 decision 8): every frame a piece draws (its own, its
 * state frames, its transition strips) on a tileset the world does not list or missing from the catalogue,
 * and every behaviour type the game does not register.
 *
 * @param template The template.
 * @param tilesets The world's tilesets.
 * @param frameExists Whether the editor's atlas holds a frame.
 * @param objectTypes The object type ids the game registers.
 * @returns The issues in piece order, empty when the template can be placed.
 */
export function templateIssues(template: PropTemplate, tilesets: readonly TilesetRef[], frameExists: (frame: TileRef) => boolean, objectTypes: readonly string[]): TemplateIssue[] {
  const listed = new Set(tilesets.map((t) => t.id));
  const issues: TemplateIssue[] = [];
  template.pieces.forEach((piece, index) => {
    const frames = [piece.frame, ...Object.values(piece.behaviour?.frames ?? {}), ...Object.values(piece.behaviour?.animations ?? {})];
    const reported = new Set<string>();
    for (const frame of frames) {
      const message = !listed.has(frame.tileset) ? `tileset ${frame.tileset} is not in this world` : frameExists(frame) ? null : `${frame.tileset} tile ${frame.tile} is not in the catalogue`;
      if (message !== null && !reported.has(message)) {
        reported.add(message);
        issues.push({ piece: index, message });
      }
    }
    if (piece.behaviour !== undefined && !objectTypes.includes(piece.behaviour.type)) {
      issues.push({ piece: index, message: `behaviour type ${piece.behaviour.type} is not registered` });
    }
  });
  return issues;
}

/**
 * One Shift+click on a set of gathered props (ADR-066 decision 5): adds the topmost prop under the pointer
 * not gathered yet, so repeated clicks dig through a stack, and once every prop there is gathered takes the
 * topmost out.
 *
 * @param selection The gathered props.
 * @param hits The props under the pointer, topmost first (`propsAt`).
 * @returns The new set; the same contents when nothing lies under the pointer.
 */
export function gatherStep(selection: readonly PropRef[], hits: readonly PropRef[]): PropRef[] {
  const same = (a: PropRef, b: PropRef): boolean => a.layerId === b.layerId && a.propId === b.propId;
  const fresh = hits.find((hit) => !selection.some((s) => same(s, hit)));
  if (fresh !== undefined) {
    return [...selection, fresh];
  }
  const top = hits[0];
  return top === undefined ? [...selection] : selection.filter((s) => !same(s, top));
}
